/**
 * VersionChecker.ts
 * 
 * 클라이언트 버전 감지 및 자동 업데이트 안내 모듈
 * - 현재 빌드 버전과 서버의 /version.json 버전을 비교
 * - 주기적 폴링 및 웹소켓 버전 불일치 시 안내 팝업 표시
 * - 확인 클릭 시 캐릭터 저장 후 메인 메뉴로 안전 복귀 및 브라우저 하드 리로드
 */

export interface VersionInfo {
  version: string;
  buildTime?: number;
  description?: string;
}

// 현재 클라이언트 빌드 버전 (코드에 번들링)
export const CLIENT_VERSION: string = '0.2.0';

export class VersionChecker {
  private static modalEl: HTMLElement | null = null;
  private static checkIntervalTimer: number | null = null;
  private static isPromptOpen = false;
  private static onExitCallback?: () => void;

  /**
   * 버전 검사 초기화
   * @param onNeedExit 업데이트가 필요하여 저장 및 메인메뉴로 이동할 때 호출할 콜백
   */
  public static init(onNeedExit: () => void): void {
    this.onExitCallback = onNeedExit;
    // 게임 시작 시 1회 체크
    this.check();
    // 30초마다 주기적 검사 (배포 후 백그라운드에서 신버전 감지)
    if (this.checkIntervalTimer !== null) {
      window.clearInterval(this.checkIntervalTimer);
    }
    this.checkIntervalTimer = window.setInterval(() => {
      this.check();
    }, 30000);
  }

  /**
   * 서버 /version.json 파일과 버전 비교
   */
  public static async check(): Promise<boolean> {
    if (this.isPromptOpen) return false;
    try {
      // 캐시 방지 타임스탬프 쿼리
      const res = await fetch(`/version.json?_t=${Date.now()}`, {
        cache: 'no-store',
      });
      if (!res.ok) return true;
      const data: VersionInfo = await res.json();
      if (data && data.version && data.version !== CLIENT_VERSION) {
        console.warn(`[VersionChecker] Version mismatch detected: Client(${CLIENT_VERSION}) vs Server(${data.version})`);
        this.triggerUpdatePrompt(data.version);
        return false;
      }
    } catch (e) {
      // 네트워크 일시 오류는 무시
    }
    return true;
  }

  /**
   * 신버전 발견 시 업데이트 필요 팝업 출력
   */
  public static triggerUpdatePrompt(serverVersion?: string): void {
    if (this.isPromptOpen) return;
    this.isPromptOpen = true;

    if (!this.modalEl) {
      const overlay = document.createElement('div');
      overlay.id = 'version-update-modal';
      overlay.style.cssText =
        'position:fixed;inset:0;z-index:999999;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,0.85);backdrop-filter:blur(6px);font-family:system-ui, -apple-system, sans-serif;user-select:none;';

      const box = document.createElement('div');
      box.style.cssText =
        'background:linear-gradient(135deg, #1e293b, #0f172a);border:2px solid #38bdf8;box-shadow:0 0 30px rgba(56,189,248,0.4);border-radius:12px;padding:28px 24px;max-width:400px;width:90%;text-align:center;color:#f8fafc;';

      box.innerHTML = `
        <div style="font-size:36px;margin-bottom:12px;">🔄</div>
        <div style="font-size:18px;font-weight:bold;color:#fde047;margin-bottom:8px;">새로운 업데이트가 있습니다</div>
        <div style="font-size:13px;line-height:1.6;color:#cbd5e1;margin-bottom:20px;word-break:keep-all;">
          업데이트가 필요합니다.<br/>
          진행 상황을 안전하게 저장하고 메인메뉴로 이동합니다.<br/>
          <span style="font-size:11px;color:#94a3b8;">(새로 적용된 최신 버전으로 자동 새로고침됩니다)</span>
        </div>
        <div style="font-size:11px;color:#38bdf8;margin-bottom:16px;">
          현재: v${CLIENT_VERSION} ${serverVersion ? `➔ 최신: v${serverVersion}` : ''}
        </div>
        <button id="btn-version-confirm" style="
          width:100%;
          padding:12px 20px;
          background:linear-gradient(135deg, #0284c7, #0ea5e9);
          border:1px solid #38bdf8;
          color:#ffffff;
          font-size:14px;
          font-weight:bold;
          border-radius:8px;
          cursor:pointer;
          box-shadow:0 4px 14px rgba(14,165,233,0.4);
          transition:all 0.15s ease;
        ">
          확인 (저장 후 이동)
        </button>
      `;

      overlay.appendChild(box);
      document.body.appendChild(overlay);
      this.modalEl = overlay;

      const confirmBtn = box.querySelector('#btn-version-confirm') as HTMLButtonElement;
      confirmBtn.addEventListener('click', () => {
        confirmBtn.disabled = true;
        confirmBtn.innerText = '저장 및 이동 중...';
        // 1. 캐릭터 저장 및 메인메뉴 복귀
        if (this.onExitCallback) {
          try {
            this.onExitCallback();
          } catch (err) {
            console.error('[VersionChecker] Error saving character on version exit:', err);
          }
        }
        // 2. 캐시를 우회하여 최신 번들로 페이지 새로고침
        setTimeout(() => {
          window.location.reload();
        }, 300);
      });
    }

    this.modalEl.style.display = 'flex';
  }
}
